import { RTC_FIRST_FRAME_STATE } from './constants'
import { extractProfileInfo, getGpuInfo } from './utils/first-frame-result'

const FIRST_FRAME_STATE_ORDER = [
  RTC_FIRST_FRAME_STATE.LOAD_START,
  RTC_FIRST_FRAME_STATE.OFFER_SDP_READY,
  RTC_FIRST_FRAME_STATE.ANSWER_SDP_RECV,
  RTC_FIRST_FRAME_STATE.ANSWER_SDP_READY,
  RTC_FIRST_FRAME_STATE.ICE_CONNECTED,
  RTC_FIRST_FRAME_STATE.DTLS_CONNECTED,
  RTC_FIRST_FRAME_STATE.PEER_CONNECTED,
  RTC_FIRST_FRAME_STATE.FIRST_PACKET_RECV,
  RTC_FIRST_FRAME_STATE.FIRST_FRAME_RECV,
  RTC_FIRST_FRAME_STATE.FIRST_FRAME_DECODED,
  RTC_FIRST_FRAME_STATE.VIDEO_LOADEDDATA,
  RTC_FIRST_FRAME_STATE.VIDEO_CANPLAY,
  RTC_FIRST_FRAME_STATE.VIDEO_PLAYING
]

export type FirstFrameStateResult = Partial<Record<RTC_FIRST_FRAME_STATE, number>>

export interface FirstFrameObserverExtraInfo {
  is_preload: boolean
  failure_reason?: string
  local_ip?: string
  local_port?: number
  local_candidate_type?: string
  remote_ip?: string
  remote_port?: number
  remote_candidate_type?: string
  protocol?: string
  stun_requests_sent?: number
  stun_requests_received?: number
  stun_responses_received?: number
  stun_responses_sent?: number
  rtt?: number
  video_codec?: string
  video_fmtp?: string
  video_profile_level_id?: string
  video_width?: number
  video_height?: number
  video_framerate?: number
  video_decoder_name?: string
  video_result?: boolean
  audio_codec?: string
  audio_fmtp?: string
  audio_packet_received?: boolean
  audio_result?: boolean
  gpu_info?: string
}

export class FirstFrameObserver {
  private _startConnectTs = 0

  private _firstVideoFrameInterval: number | null = null

  private _preState: RTC_FIRST_FRAME_STATE | null = null

  private _state: FirstFrameStateResult = {}

  private _pc: RTCPeerConnection | null = null

  private _video: HTMLVideoElement | null = null

  private _videoTransceiver: RTCRtpTransceiver | null = null

  private _audioTransceiver: RTCRtpTransceiver | null = null

  private _extraInfo: FirstFrameObserverExtraInfo = {
    is_preload: false,
  }

  constructor(
    private _emitFirstFrameState: (
      info: FirstFrameStateResult,
      extraInfo: FirstFrameObserverExtraInfo
    ) => void
  ) {}

  public start() {
    this.reset()
    this._startConnectTs = Date.now()
    this.changeFirstFrameState(RTC_FIRST_FRAME_STATE.LOAD_START)
  }

  /**
   * 上报首帧状态
   * 为了防止首帧状态乱序，需要保证上报的状态是递增的
   * @param {RTC_FIRST_FRAME_STATE} state 状态
   */
  public changeFirstFrameState(state: RTC_FIRST_FRAME_STATE) {
    const preIdx =
      (this._preState && FIRST_FRAME_STATE_ORDER.indexOf(this._preState)) ?? -1
    const curIdx = FIRST_FRAME_STATE_ORDER.indexOf(state)
    const elapsed = Date.now() - this._startConnectTs
    if (curIdx > preIdx) {
      FIRST_FRAME_STATE_ORDER.slice(preIdx + 1, curIdx + 1).forEach(s => {
        this._state[s] = elapsed
      })
      this._preState = state
    }
  }

  public setIsPreload(isPreload: boolean) {
    this._extraInfo.is_preload = isPreload
  }

  public setFailureReason(reason: string) {
    this._extraInfo.failure_reason = reason
  }

  public bindEvents(
    pc: RTCPeerConnection,
    videoTransceiver: RTCRtpTransceiver | null,
    audioTransceiver: RTCRtpTransceiver | null,
    video: HTMLVideoElement | null
  ) {
    this._pc = pc
    this._video = video
    this._videoTransceiver = videoTransceiver
    this._audioTransceiver = audioTransceiver

    pc.addEventListener('connectionstatechange', this._handleConnectionStateChange)

    pc.addEventListener('iceconnectionstatechange', this._handleICEConnectionStateChange)

    const { transport } = videoTransceiver?.receiver || {}
    if (transport) {
      transport.onstatechange = () => {
        transport.state === 'connected' &&
          this.changeFirstFrameState(RTC_FIRST_FRAME_STATE.DTLS_CONNECTED)
      }
    }

    video?.addEventListener('playing', this._handlePlaying)

    video?.addEventListener('canplay', this._handleCanplay)

    video?.addEventListener('loadeddata', this._handleLoadeddata)

    video?.addEventListener('error', this._handleError)
  }

  private _handleConnectionStateChange = () => {
    if (this._pc?.connectionState === 'connected' && this._videoTransceiver) {
      this.changeFirstFrameState(RTC_FIRST_FRAME_STATE.PEER_CONNECTED)
      this._watchForFirstVideoFrameRecv(this._videoTransceiver)
    }
  }

  private _handleICEConnectionStateChange = () => {
    if (this._pc?.iceConnectionState === 'connected') {
      this.changeFirstFrameState(RTC_FIRST_FRAME_STATE.ICE_CONNECTED)
    }
  }

  private _handlePlaying = () => {
    this.changeFirstFrameState(RTC_FIRST_FRAME_STATE.VIDEO_PLAYING)
    this.reset()
  }

  private _handleCanplay = () => {
    this.changeFirstFrameState(RTC_FIRST_FRAME_STATE.VIDEO_CANPLAY)
  }

  private _handleLoadeddata = () => {
    this.changeFirstFrameState(RTC_FIRST_FRAME_STATE.VIDEO_LOADEDDATA)
  }

  private _handleError = () => {
    this.reset()
  }

  private _watchForFirstVideoFrameRecv(videoTransceiver: RTCRtpTransceiver) {
    let hasPacketsReceived = false
    let hasFrameReceived = false
    let hasFrameDecoded = false
    this._cancelWatchForFirstVideoFrameRecv()
    this._firstVideoFrameInterval = window.setInterval(async () => {
      const receiver = videoTransceiver.receiver
      const reports = await receiver.getStats()
      let inboundRtpStats: any
      reports.forEach(report => {
        if (report.type === 'inbound-rtp') {
          inboundRtpStats = report
        }
      })
      if (!hasPacketsReceived && inboundRtpStats?.packetsReceived > 0) {
        hasPacketsReceived = true
        this.changeFirstFrameState(RTC_FIRST_FRAME_STATE.FIRST_PACKET_RECV)
      }
      if (!hasFrameReceived && inboundRtpStats?.framesReceived > 0) {
        hasFrameReceived = true
        this.changeFirstFrameState(RTC_FIRST_FRAME_STATE.FIRST_FRAME_RECV)
      }
      if (!hasFrameDecoded && inboundRtpStats?.framesDecoded > 0) {
        hasFrameDecoded = true
        this.changeFirstFrameState(RTC_FIRST_FRAME_STATE.FIRST_FRAME_DECODED)
        this._cancelWatchForFirstVideoFrameRecv()
      }
    }, 200)
  }

  private _cancelWatchForFirstVideoFrameRecv() {
    if (this._firstVideoFrameInterval) {
      window.clearInterval(this._firstVideoFrameInterval)
      this._firstVideoFrameInterval = null
    }
  }

  private async _getTransportInfo() {
    const reports = await this._videoTransceiver?.receiver?.getStats()
    let candidatePairStats: undefined | Record<string, any>
    let localCandidateStats: undefined | Record<string, any>
    let remoteCandidateStats: undefined | Record<string, any>
    let inboundRtpStats: undefined | Record<string, any>
    let codecStats: undefined | Record<string, any>
    reports?.forEach(report => {
      if (report.type === 'candidate-pair') {
        candidatePairStats = report
        localCandidateStats = reports.get(report.localCandidateId)
        remoteCandidateStats = reports.get(report.remoteCandidateId)
      } else if (report.type === 'inbound-rtp') {
        inboundRtpStats = report
        codecStats = reports.get(report.codecId)
      }
    })
    if (remoteCandidateStats) {
      this._extraInfo.remote_ip = remoteCandidateStats.ip || remoteCandidateStats.address
      this._extraInfo.remote_port = remoteCandidateStats.port
      this._extraInfo.remote_candidate_type = remoteCandidateStats.candidateType
      this._extraInfo.protocol = remoteCandidateStats.protocol
    }
    if (localCandidateStats) {
      this._extraInfo.local_ip = localCandidateStats.ip || localCandidateStats.address
      this._extraInfo.local_port = localCandidateStats.port
      this._extraInfo.local_candidate_type = localCandidateStats.candidateType
    }
    if (candidatePairStats) {
      this._extraInfo.rtt = candidatePairStats.currentRoundTripTime
      this._extraInfo.stun_requests_sent = candidatePairStats.requestsSent
      this._extraInfo.stun_requests_received = candidatePairStats.requestsReceived
      this._extraInfo.stun_responses_sent = candidatePairStats.responsesSent
      this._extraInfo.stun_responses_received = candidatePairStats.responsesReceived
    }

    if (codecStats) {
      this._extraInfo.video_codec = codecStats.mimeType
      this._extraInfo.video_fmtp = codecStats.sdpFmtpLine
      this._extraInfo.video_profile_level_id = extractProfileInfo(codecStats.sdpFmtpLine)
    }
    if (inboundRtpStats) {
      this._extraInfo.video_width = inboundRtpStats.frameWidth
      this._extraInfo.video_height = inboundRtpStats.frameHeight
      this._extraInfo.video_framerate = inboundRtpStats.framesPerSecond
      this._extraInfo.video_decoder_name = inboundRtpStats.decoderImplementation
      this._extraInfo.video_result =
        (inboundRtpStats?.framesDecoded as number) > 0 ? true : false
    }
    this._extraInfo.gpu_info = getGpuInfo().renderer
  }

  private async _getAudioCodecInfo() {
    const reports = await this._audioTransceiver?.receiver?.getStats()
    let inboundRtpStats: undefined | Record<string, any>
    let codecStats: undefined | Record<string, any>
    reports?.forEach(report => {
      if (report.type === 'inbound-rtp') {
        inboundRtpStats = report
        codecStats = reports.get(report.codecId)
      }
    })

    if (codecStats) {
      this._extraInfo.audio_codec = codecStats.mimeType
      this._extraInfo.audio_fmtp = codecStats.sdpFmtpLine
    }
    if (inboundRtpStats) {
      this._extraInfo.audio_packet_received =
        (inboundRtpStats?.packetsReceived as number) > 0
      this._extraInfo.audio_result =
        inboundRtpStats?.audioLevel !== 0 ||
        inboundRtpStats?.totalAudioEnergy !== 0 ||
        ((inboundRtpStats?.totalSamplesReceived as number) > 0 &&
          inboundRtpStats?.totalSamplesReceived !==
            (inboundRtpStats?.concealedSamples as number))
          ? true
          : false
    }
  }

  /**
   * 尝试上报并重置状态
   * 开始检测or检测结束时调用
   */
  public async reset() {
    if (Object.keys(this._state).length > 0) {
      await this._getTransportInfo()
      await this._getAudioCodecInfo()
      this._emitFirstFrameState(this._state, this._extraInfo)
    }
    this._preState = null
    this._extraInfo = {
      is_preload: false,
    }
    this._state = {}
    this._video?.removeEventListener('playing', this._handlePlaying)
    this._video?.removeEventListener('canplay', this._handleCanplay)
    this._video?.removeEventListener('loadeddata', this._handleLoadeddata)
    this._video?.removeEventListener('error', this._handleError)

    this._pc?.removeEventListener(
      'connectionstatechange',
      this._handleConnectionStateChange
    )
    this._pc?.removeEventListener(
      'iceconnectionstatechange',
      this._handleICEConnectionStateChange
    )

    this._cancelWatchForFirstVideoFrameRecv()

    this._pc = null
    this._video = null
    this._videoTransceiver = null
    this._audioTransceiver = null
  }
}
